/**
 * Auth Configuration Service
 *
 * Dynamically fetches Cognito domain configuration from the backend (GET /auth/config).
 * Falls back to process.env.REACT_APP_COGNITO_DOMAIN if offline or unreachable.
 */

import { getApiUrl } from '../utils/api';
import { logger } from '../utils/logger';

export interface AuthConfig {
  cognitoDomain: string;
}

class AuthConfigService {
  private cognitoDomain: string;
  private isFetched = false;
  private fetchPromise: Promise<string> | null = null;

  constructor() {
    this.cognitoDomain = (process.env.REACT_APP_COGNITO_DOMAIN || '').replace(/\/$/, '');
  }

  /**
   * Synchronously return the current Cognito domain (fallback or remotely fetched).
   */
  public getCognitoDomain(): string {
    return this.cognitoDomain;
  }

  /**
   * Asynchronously return the Cognito domain, ensuring remote fetch is resolved if in progress.
   */
  public async getCognitoDomainAsync(): Promise<string> {
    if (this.isFetched) {
      return this.cognitoDomain;
    }
    return this.fetchRemoteConfig();
  }

  /**
   * Fetch runtime auth config from backend GET /auth/config.
   * Safe to call multiple times; uses in-flight promise and caches result in-memory.
   */
  public async fetchRemoteConfig(): Promise<string> {
    if (this.isFetched) {
      return this.cognitoDomain;
    }
    if (this.fetchPromise) {
      return this.fetchPromise;
    }

    this.fetchPromise = (async () => {
      try {
        const apiUrl = getApiUrl();
        if (!apiUrl) {
          logger.warn('AuthConfigService', 'No API URL configured, using fallback Cognito domain');
          return this.cognitoDomain;
        }

        const response = await fetch(`${apiUrl}/auth/config`, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        });

        if (response.ok) {
          const data = (await response.json()) as AuthConfig;
          if (data?.cognitoDomain && typeof data.cognitoDomain === 'string') {
            this.cognitoDomain = data.cognitoDomain.replace(/\/$/, '');
            this.isFetched = true;
            logger.info(
              'AuthConfigService',
              `Loaded dynamic Cognito domain: ${this.cognitoDomain}`,
            );
          }
        } else {
          logger.warn(
            'AuthConfigService',
            `Failed to fetch /auth/config (${response.status}), using fallback`,
          );
        }
      } catch (error) {
        logger.warn('AuthConfigService', 'Error fetching /auth/config, using fallback', error);
      } finally {
        this.fetchPromise = null;
      }

      return this.cognitoDomain;
    })();

    return this.fetchPromise;
  }
}

export const authConfigService = new AuthConfigService();

// Pre-fetch on module load
authConfigService.fetchRemoteConfig();
